import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { School } from '../types/schools/School';
import { preloadSchoolLogos } from '../utils/imagePreloader';
import { useUser } from './UserContext';

interface SchoolsContextUSType {
  schools: School[];
  loading: boolean;
  error: string | null;
  refreshSchools: () => void;
  loadFeedIconsForSchools: (schoolNames: string[]) => Promise<void>;
  feedIconsLoading: boolean;
}

const SchoolsContextUS = createContext<SchoolsContextUSType | undefined>(undefined);

export const useSchoolsUS = () => {
  const context = useContext(SchoolsContextUS);
  if (context === undefined) {
    throw new Error('useSchoolsUS must be used within a SchoolsProviderUS');
  }
  return context;
};

interface SchoolsProviderUSProps {
  children: ReactNode;
}

export const SchoolsProviderUS: React.FC<SchoolsProviderUSProps> = ({ children }) => {
  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedIconsLoading, setFeedIconsLoading] = useState(false);
  const [feedIconsLoaded, setFeedIconsLoaded] = useState<Set<string>>(new Set());
  const { user } = useUser();

  const fetchSchools = async () => {
    console.log('🔄 SchoolsContextUS: fetchSchools called, user:', user?.uid);
    
    // Don't fetch if user is not authenticated
    if (!user) {
      console.log('❌ SchoolsContextUS: No user, skipping fetch');
      setLoading(false);
      setSchools([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const db = getDatabase();
      
      // Fetch US schools, feed icons, and athletics in parallel
      const [schoolsSnapshot, feedIconSnapshot, athleticsSnapshot] = await Promise.all([
        get(ref(db, 'schools_US')),
        get(ref(db, 'schoolFeedIcon')),
        get(ref(db, 'athletics'))
      ]);

      if (schoolsSnapshot.exists()) {
        const schoolsData = schoolsSnapshot.val();
        const feedIconData = feedIconSnapshot.exists() ? feedIconSnapshot.val() : {};
        const athleticsData = athleticsSnapshot.exists() ? athleticsSnapshot.val() : {};

        const schoolsArray: School[] = Object.entries(schoolsData)
          .filter(([id, data]: [string, any]) => {
            // Filter out schools that have been archived (have a timestamp in SchoolLastDeleted)
            return !data.SchoolLastDeleted;
          })
          .map(([id, data]: [string, any]) => {
            // Use the schoolID from the school data if available, otherwise fallback to the id from the schools_US table
            const schoolID = data.SchoolID || id;
            
            return {
              id,
              name: data.SchoolName || data.name || '',
              city: data.SchoolCity || data.city || '',
              province: data.SchoolProvinceState || data.province || data.state || data.State || '',
              type: data.SchoolType || data.type || 'University',
              enrollment: data.SchoolFTUGCount || data.enrollment || 0,
              acceptanceRate: data.acceptanceRate || 0,
              admissionsMinimum: data.SchoolAdmissionsMin || '',
              tuition: data.tuition || 0,
              roomAndBoardLow: data.SchoolRoomAndBoardLow || data.roomAndBoardLow || 0,
              roomAndBoardHigh: data.SchoolRoomAndBoardHigh || data.roomAndBoardHigh || 0,
              description: data.SchoolDescription || data.description || '',
              resultsImage: data.SchoolResultsImageURL || data.resultsImage || '',
              programs: data.programs || [],
              scholarships: data.scholarships || [],
              events: data.events || [],
              website: data.SchoolWebsite || data.website || '',
              image: data.SchoolProfileImageURL || data.image || '',
              socialMedia: [], // You can map social media fields here if needed
              athletics: [], // Will be populated below
              admissionRequirements: Array.isArray(data.SchoolBasicRequirements) 
                ? data.SchoolBasicRequirements 
                : (data.SchoolBasicRequirements ? [data.SchoolBasicRequirements] : []),
              feedIcon: feedIconData[schoolID] || '', // Load feed icon immediately
              athleticsNickname: data.AthleticsNickname || data.athleticsNickname || '',
              athleticsWebsite: data.AthleticsWebsite || data.athleticsWebsite || '',
              SchoolWikiURL: data.SchoolWikiURL || data.SchoolWikiURL || '',
              SchoolYoutubeURL: data.SchoolYoutubeURL || data.SchoolYoutubeURL || '',
              SchoolTwitterURL: data.SchoolTwitterURL || data.SchoolTwitterURL || '',
              SchoolInstagramURL: data.SchoolInstagramURL || data.SchoolInstagramURL || '',
              SchoolPhone: data.SchoolPhone || data.SchoolPhone || '',
              SchoolLocationLatitude: data.SchoolLocationLatitude || data.SchoolLocationLatitude || '',
              SchoolLocationLongitude: data.SchoolLocationLongitude || data.SchoolLocationLongitude || '',
              SchoolLastDeleted: data.SchoolLastDeleted || undefined,
              SchoolResultsImageURL: data.SchoolResultsImageURL || data.SchoolResultsImageURL || '',
              primaryLanguage: data.primaryLanguage || '',
              ...data
            };
          });

        // Populate athletics data for each school
        const schoolsWithAthletics = schoolsArray.map(school => {
          const schoolAthletics = Object.values(athleticsData).filter((a: any) => 
            a.AthleticsSchoolName === school.name
          ).map((a: any) => ({
            sport: a.AthleticsSport,
            gender: a.AthleticsGender === 'M' ? 'Male'
                  : a.AthleticsGender === 'W' ? 'Female'
                  : a.AthleticsGender // fallback for 'Both' or other values
          }));
          return { ...school, athletics: schoolAthletics };
        });
        
        // Mark all feed icons as loaded since we fetched them all
        const allSchoolNames = schoolsWithAthletics.map(school => school.name);
        setFeedIconsLoaded(new Set(allSchoolNames));
        setSchools(schoolsWithAthletics);
        
        console.log('✅ SchoolsContextUS: Loaded', schoolsWithAthletics.length, 'US schools with feed icons and athletics data');
        console.log('📊 Sample US school data:', schoolsWithAthletics[0]);
        
        // Debug: Log admissionsMinimum data for first few schools
        schoolsWithAthletics.slice(0, 3).forEach((school, index) => {
          console.log(`🔍 School ${index + 1} (${school.name}): admissionsMinimum =`, school.admissionsMinimum);
        });
        
        // Preload school logos in the background for better performance
        preloadSchoolLogos(schoolsWithAthletics).catch(error => {
          console.warn('⚠️ Failed to preload some US school logos:', error);
        });
      } else {
        setSchools([]);
      }
    } catch (err) {
      console.error('Error fetching US schools:', err);
      setError('Failed to fetch US schools data');
    } finally {
      setLoading(false);
    }
  };

  const loadFeedIconsForSchools = async (schoolNames: string[]) => {
    // Since we now load all feed icons upfront, this function is mainly for edge cases
    // where a school might not have been loaded initially
    try {
      setFeedIconsLoading(true);
      console.log('🔄 SchoolsContextUS: Checking for missing feed icons for US schools:', schoolNames);
      
      const db = getDatabase();
      const schoolFeedIconRef = ref(db, 'schoolFeedIcon');
      const feedIconSnapshot = await get(schoolFeedIconRef);
      const feedIconData = feedIconSnapshot.exists() ? feedIconSnapshot.val() : {};

      // Only update schools that don't already have feed icons
      setSchools(prevSchools => 
        prevSchools.map(school => {
          if (schoolNames.includes(school.name) && !school.feedIcon && !feedIconsLoaded.has(school.name)) {
            const schoolID = school.id;
            const feedIcon = feedIconData[schoolID] || '';
            setFeedIconsLoaded(prev => new Set(Array.from(prev).concat([school.name])));
            return { ...school, feedIcon };
          }
          return school;
        })
      );
      
      console.log('✅ SchoolsContextUS: Updated feed icons for missing US schools');
    } catch (error) {
      console.error('❌ SchoolsContextUS: Error loading feed icons:', error);
    } finally {
      setFeedIconsLoading(false);
    }
  };

  const refreshSchools = () => {
    fetchSchools();
  };

  useEffect(() => {
    fetchSchools();
  }, [user]);

  const value: SchoolsContextUSType = {
    schools,
    loading,
    error,
    refreshSchools,
    loadFeedIconsForSchools,
    feedIconsLoading,
  };

  return (
    <SchoolsContextUS.Provider value={value}>
      {children}
    </SchoolsContextUS.Provider>
  );
};
